import React from 'react';

/** A one-shot expand/collapse-all instruction broadcast to every registered section. */
interface DisclosureCommand {
    /** Monotonic sequence so sections apply each command exactly once. */
    seq: number;
    /** Desired open state for all sections. */
    open: boolean;
}

interface FindingsDisclosureContextValue {
    /** Latest expand/collapse-all command, or null when none has been issued. */
    command: DisclosureCommand | null;
    /** Namespace for per-section localStorage keys (e.g. `attn:<migrationId>`). */
    storagePrefix: string;
    /** Opens every registered section. */
    expandAll: () => void;
    /** Collapses every registered section. */
    collapseAll: () => void;
}

const FindingsDisclosureContext = React.createContext<FindingsDisclosureContextValue | null>(null);

/** Returns the ambient disclosure controller, or null when rendered outside a provider. */
export const useFindingsDisclosure = (): FindingsDisclosureContextValue | null =>
    React.useContext(FindingsDisclosureContext);

interface FindingsDisclosureProviderProps {
    /** Namespace for per-section localStorage keys so expand state is scoped per migration. */
    storagePrefix: string;
    children: React.ReactNode;
}

/**
 * Coordinates the collapsible findings sections in the migration-attention modal: it broadcasts
 * expand-all / collapse-all commands and provides the localStorage namespace each section uses to
 * remember its open state across reopenings. Sections work without a provider (no persistence, no
 * bulk toggle); the provider simply adds those capabilities.
 */
export const FindingsDisclosureProvider: React.FC<FindingsDisclosureProviderProps> = ({
    storagePrefix,
    children,
}) => {
    const [command, setCommand] = React.useState<DisclosureCommand | null>(null);

    const expandAll = React.useCallback(() => {
        setCommand((prev) => ({ seq: (prev?.seq ?? 0) + 1, open: true }));
    }, []);
    const collapseAll = React.useCallback(() => {
        setCommand((prev) => ({ seq: (prev?.seq ?? 0) + 1, open: false }));
    }, []);

    const value = React.useMemo<FindingsDisclosureContextValue>(
        () => ({ command, storagePrefix, expandAll, collapseAll }),
        [command, storagePrefix, expandAll, collapseAll],
    );

    return (
        <FindingsDisclosureContext.Provider value={value}>{children}</FindingsDisclosureContext.Provider>
    );
};
